<template>
  <v-container fluid class="pt-0 px-0">
    <v-app-bar color="white">
      <v-container class="pa-0">
        <v-row dense elevation="6">
          <v-col cols="auto">
            <v-btn text @click="goBack" color="secondary">
              <v-icon class="mr-2">keyboard_arrow_left</v-icon>
              Go back
            </v-btn>
          </v-col>
          <v-spacer></v-spacer>
          <template v-if="feature && feature.id == $enums.features.PULSE">
            <v-col
              cols="auto"
              v-if="scheduled_pulse"
              class="secondarytext--text my-auto"
            >
              Next Send: {{ scheduled_pulse.scheduleDate | longDate }}
            </v-col>
            <v-col
              cols="auto"
              v-if="pulse_overview.previewLink"
              class="ml-2 my-auto"
            >
              <v-btn
                :href="pulse_overview.previewLink"
                outlined
                color="secondary"
                target="_blank"
              >
                Preview Survey
              </v-btn>
            </v-col>
          </template>
          <template v-if="feature && feature.id == $enums.features.REVIEW">
            <v-col cols="auto" class="ml-2 my-auto">
              <v-btn
                to="/features/reviews/settings/questions/preview"
                outlined
                color="secondary"
              >
                Preview
              </v-btn>
            </v-col>
          </template>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-container v-if="feature">
      <v-row v-if="detail" dense>
        <v-col cols="12">
          <ad-header-card :title="detail.title" :subtitle="detail.subtitle">
            <v-alert
              dense
              text
              :color="detail.alert_color"
              :icon="detail.alert_icon"
              v-if="detail.alert_text"
              class="subtitle-1 mx-4"
            >
              <span v-html="detail.alert_text"></span>
            </v-alert>
          </ad-header-card>
        </v-col>
        <v-col cols="12">
          <template v-if="feature.id == $enums.features.PULSE">
            <pulse-schedule
              v-if="detail.id == 'schedule'"
              v-on:pause="loadDataAndReset"
              v-on:save="loadDataAndReset"
            ></pulse-schedule>
            <questions
              v-if="detail.id == 'questions'"
              :featureId="$enums.features.PULSE"
            ></questions>
            <pulse-high-low-categories
              v-if="detail.id == 'high-low'"
              v-on:save="loadDataAndReset"
            >
            </pulse-high-low-categories>
          </template>
          <template v-else-if="feature.id == $enums.features.REVIEW">
            <review-settings
              v-if="detail.id == 'schedule'"
              v-on:pause="loadDataAndReset"
              v-on:save="loadDataAndReset"
            ></review-settings>
            <questions
              v-if="detail.id == 'questions'"
              :featureId="$enums.features.REVIEW"
            ></questions>
          </template>
          <template v-else-if="feature.id == $enums.features.ANNUAL_REVIEWS">
            <annual-review-settings
              v-if="detail.id == 'schedule'"
            ></annual-review-settings>
            <annual-review-ratings
              v-if="detail.id == 'ratings'"
            ></annual-review-ratings>
            <annual-review-settings-questions
              v-if="detail.id == 'questions'"
            ></annual-review-settings-questions>
          </template>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col cols="12">
          <ad-header-card :title="feature.title" :subtitle="feature.subtitle">
            <template v-slot:titleExtension>
              <v-img
                :src="feature.image"
                max-width="25"
                class="ml-2"
                v-if="feature.image"
              >
              </v-img>
            </template>
            <v-alert
              dense
              text
              color="success"
              icon="info"
              v-if="feature.enabled"
              class="subtitle-1 ml-4"
            >
              You can now use this feature! We've also added a link in the
              dashboard and main navigation (under features), for easy access.
            </v-alert>
          </ad-header-card>
        </v-col>
        <v-col cols="12">
          <v-card>
            <feature-list-item
              icon="power_settings_new"
              title="Enable or Disable"
              subtitle="This status controls whether or not this feature will be available in your account."
            >
              <template v-slot:action>
                <v-select
                  v-model="feature.enabled"
                  :items="enabledItems"
                  outlined
                  height="35"
                  hide-details
                  rounded
                  @change="saveSettings"
                  class="no-width subtitle-2 font-weight-light secondarytext--text"
                  :color="feature.enabled ? 'success' : 'error'"
                >
                  <template v-slot:selection="{ item }">
                    <v-icon
                      class="mr-2"
                      small
                      :color="item.value ? 'success' : 'error'"
                    >
                      fiber_manual_record
                    </v-icon>
                    {{ item.text }}
                  </template>
                </v-select>
              </template>
              <template v-slot:helpText v-if="feature.enableAdditionalText">
                {{ feature.enableAdditionalText }}
              </template>
            </feature-list-item>
            <template
              v-if="
                feature.enabled && feature.items && feature.items.length > 0
              "
            >
              <v-divider></v-divider>
              <template v-for="(item, i) in feature.items">
                <feature-list-item
                  :key="i"
                  :icon="item.icon"
                  :title="item.title"
                  :subtitle="item.subtitle"
                  :url="`/features/${feature.url}/settings/${item.id}`"
                  action-icon="edit"
                  :help-text-color="item.help_text_color || 'accent'"
                >
                  <template v-slot:helpText v-if="item.additionalText">
                    {{ item.additionalText }}
                  </template>
                </feature-list-item>
                <v-divider
                  :key="i + 'divider'"
                  v-if="i < feature.items.length - 1"
                ></v-divider>
              </template>
            </template>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import AdHeaderCard from "@/components/common/AdHeaderCard";
import FeatureListItem from "@/components/common/FeatureListItem";
import Questions from "@/components/questions/Questions";
import PulseSchedule from "@/components/pulse/PulseSchedule";
import PulseHighLowCategories from "@/components/pulse/PulseHighLowCategories";
import ReviewSettings from "@/components/reviews/ReviewSettings";
import AnnualReviewSettings from "@/components/annualreviews/AnnualReviewSettings";
import AnnualReviewRatings from "@/components/annualreviews/AnnualReviewRatings";
import AnnualReviewSettingsQuestions from "@/components/annualreviews/AnnualReviewSettingsQuestions";
import { mapGetters } from "vuex";
export default {
  name: "Settings",
  components: {
    AdHeaderCard,
    FeatureListItem,
    Questions,
    PulseSchedule,
    PulseHighLowCategories,
    ReviewSettings,
    AnnualReviewSettings,
    AnnualReviewRatings,
    AnnualReviewSettingsQuestions
  },
  data: () => ({
    loading: false,
    enabledItems: [
      { value: true, text: "Enabled" },
      { value: false, text: "Disabled" }
    ]
  }),
  computed: {
    detail() {
      if (!this.feature) {
        return null;
      }
      switch (this.feature.id) {
        case this.$enums.features.PULSE:
          switch (this.$route.params.id) {
            case "questions":
              return {
                id: this.$route.params.id,
                title: "Choose your tracking questions",
                subtitle: `Personalize your experience below by selecting the questions you'd like to get continuous feedback on. You can change this anytime.`,
                alert_text:
                  "All questions are enabled by default. You can make changes to your questions at anytime.",
                alert_icon: "info",
                alert_color: "accent"
              };
            case "schedule":
              return {
                id: this.$route.params.id,
                title: "Launch Culture Tracking 🚀",
                subtitle: `Set your details below to turn on Culture Tracking for your company. You can adjust these settings at anytime.`,
                alert_text:
                  this.pulse_overview.employeeCount > 3
                    ? `All <span class='text-decoration-underline'>${this.pulse_overview.employeeCount} employees</span> in your account will receive tracking surveys. New members will automatically receive future tracking surveys.`
                    : `Employee count low. Please <a class='text-decoration-underline error--text' href='/company/members'>add more people</a> into CoreCounts in order to get the most out of your data`,
                alert_icon:
                  this.pulse_overview.employeeCount > 3 ? "info" : "error",
                alert_color:
                  this.pulse_overview.employeeCount > 3 ? "accent" : "error"
              };
            case "high-low":
              return {
                id: this.$route.params.id,
                title: "High & Low Categories",
                subtitle: `Determine which categories should be shown in the High & Low report.`
              };
          }
          break;
        case this.$enums.features.REVIEW:
          switch (this.$route.params.id) {
            case "questions":
              return {
                id: this.$route.params.id,
                title: "Choose your 360 review questions",
                subtitle: `Personalize your experience below by selecting the questions you'd like to get continuous feedback on. You can change this anytime.`
              };
            case "schedule":
              return {
                id: this.$route.params.id,
                title: "Launch 360 Feeback 🚀",
                subtitle: `Set your details below to turn on 360 Feeback for your company. You can adjust these settings at anytime.`
              };
          }
          break;
        case this.$enums.features.ANNUAL_REVIEWS:
          switch (this.$route.params.id) {
            case "questions":
              return {
                id: this.$route.params.id,
                title: "Define your annual review questions",
                subtitle: `Personalize your experience below by selecting the questions you'd like to get continuous feedback on. You can change this anytime.`
              };
            case "schedule":
              return {
                id: this.$route.params.id,
                title: "Launch Annual Reviews 🚀",
                subtitle: `Set your details below to turn on 360 Feeback for your company. You can adjust these settings at anytime.`
              };
            case "ratings":
              return {
                id: this.$route.params.id,
                title: "Define your annual review ratings",
                subtitle: `Set your details below to turn on 360 Feeback for your company. You can adjust these settings at anytime.`
              };
          }
          break;
      }
      return null;
    },
    feature() {
      switch (this.$route.params.feature) {
        case "tracking":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.PULSE,
            title: "Manage Culture Tracking",
            subtitle:
              "Customize your culture tracking questions, send frequency, status and more.",
            image: this.$images.PULSE_ICON,
            enabled: this.settings.isPulseActive,
            enableAdditionalText: "Additional setup is required after enabling",
            items: [
              {
                title: "Manage & Define Questions",
                subtitle:
                  "Collect continuous feedback from your employees & get a better view into your company culture",
                icon: "rate_review",
                id: "questions",
                additionalText: `${this.pulse_overview.enabledQuestionCount} of ${this.pulse_overview.questionCount} questions enabled`
              },
              {
                title: "Frequency & Schedule",
                subtitle: `Control when and how often your culture tracking survey will go out`,
                icon: "today",
                id: "schedule",
                help_text_color:
                  this.scheduled_pulse == null ? "primary" : "accent",
                additionalText:
                  this.scheduled_pulse == null
                    ? "Not Scheduled"
                    : `Next Send: ${this.$options.filters.longDate(
                        this.scheduled_pulse.scheduleDate
                      )}`
              },
              {
                title: "High & Low Categories",
                subtitle: `Control which categories will show in the High & Low Report`,
                icon: "category",
                id: "high-low",
                help_text_color:
                  this.pulse_overview.highLowCount == 0 ? "primary" : "accent",
                additionalText: `${this.pulse_overview.highLowCount} categories enabled`
              }
            ]
          };
        case "surveys":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.SURVEYS,
            title: "Create Internal Surveys 📋",
            subtitle:
              "Learn from the people within your company by creating your own custom surveys within CoreCounts",
            enabled: this.settings.isSurveysActive,
            items: []
          };
        case "recognition":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.RECOGNITION,
            title: "Employee Recognition",
            subtitle:
              "Make it easy for anyone in your organization to recognize another employee's performance",
            enabled: this.settings.isRecognitionActive,
            items: []
          };
        case "reviews":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.REVIEW,
            title: "360 Feedback",
            subtitle:
              "Easily collect continuous performance metrics on the employees across your organization.",
            enabled: this.settings.isReviewActive,
            items: [
              {
                title: "Manage & Define Questions",
                subtitle:
                  "Collect continuous feedback from your employees & get a better view into your company culture",
                icon: "rate_review",
                id: "questions"
              },
              {
                title: "Frequency & Schedule",
                subtitle: `Whether you're looking for lunch ideas or employee feedback our survey builder makes it easy`,
                icon: "today",
                id: "schedule"
              }
            ]
          };
        case "meetings":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.CHECK_IN,
            title: "Meeting Spaces",
            subtitle:
              "Ensure your team has an organized space to collaborate, manage goals & stay aligned.",
            enabled: this.settings.isCheckInActive,
            items: []
          };
        case "goals":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.GOALS,
            title: "Goal Tracking",
            subtitle:
              "Keep your team focused, set expectations and measure the progress along the way.",
            enabled: this.settings.isGoalsActive,
            items: []
          };
        case "training":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.RESOURCE,
            title: "Training Resources",
            subtitle:
              "Use our library of hand picked training resources from around the web & easily share them with your team",
            enabled: this.settings.isResourcesActive,
            items: []
          };
        case "annual-reviews":
          return {
            url: this.$route.params.feature,
            id: this.$enums.features.ANNUAL_REVIEWS,
            title: "Annual Reviews",
            subtitle:
              "Simplify the review process by removing the bottlenecks & improving your view into each employee.",
            enabled: this.settings.isAnnualReviewActive,
            items: [
              {
                title: "Schedule",
                subtitle: `Whether you're looking for lunch ideas or employee feedback our survey builder makes it easy`,
                icon: "today",
                id: "schedule"
              },
              {
                title: "Manage & Define Questions",
                subtitle:
                  "Collect continuous feedback from your employees & get a better view into your company culture",
                icon: "rate_review",
                id: "questions"
              },
              {
                title: "Manage Ratings",
                subtitle:
                  "Collect continuous feedback from your employees & get a better view into your company culture",
                icon: "star_rate",
                id: "ratings"
              }
            ]
          };
      }
      return null;
    },
    scheduled_pulse() {
      return this.pulses.find(x => x.sentDate == null);
    },
    ...mapGetters({
      pulse_overview: "pulse/overview",
      pulses: "pulse/pulses",
      review_overview: "review/overview",
      settings: "profile/settings"
    })
  },
  created() {
    this.loadData();
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    loadDataAndReset() {
      this.loadData(function(self) {
        self.goBack();
      });
    },
    loadData(callback) {
      this.loading = true;
      let callArray = [];
      if (this.feature) {
        switch (this.feature.id) {
          case this.$enums.features.PULSE:
            callArray.push(this.$store.dispatch("pulse/getOverview"));
            break;
          case this.$enums.features.REVIEW:
            callArray.push(this.$store.dispatch("review/getReviewOverview"));
            break;
        }
      }
      if (callArray.length == 0) {
        this.loading = false;
        if (callback) {
          callback(this);
        }
        return;
      }
      Promise.all(callArray)
        .then(() => {
          if (callback) {
            callback(this);
          }
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while loading culture tracking."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveSettings() {
      if (!this.feature) {
        return;
      }
      switch (this.feature.id) {
        case this.$enums.features.PULSE:
          this.settings.isPulseActive = this.feature.enabled;
          break;
        case this.$enums.features.SURVEYS:
          this.settings.isSurveysActive = this.feature.enabled;
          break;
        case this.$enums.features.RECOGNITION:
          this.settings.isRecognitionActive = this.feature.enabled;
          break;
        case this.$enums.features.REVIEW:
          this.settings.isReviewActive = this.feature.enabled;
          break;
        case this.$enums.features.CHECK_IN:
          this.settings.isCheckInActive = this.feature.enabled;
          break;
        case this.$enums.features.GOALS:
          this.settings.isGoalsActive = this.feature.enabled;
          break;
        case this.$enums.features.RESOURCE:
          this.settings.isResourcesActive = this.feature.enabled;
          break;
        case this.$enums.features.ANNUAL_REVIEWS:
          this.settings.isAnnualReviewActive = this.feature.enabled;
          break;
      }
      this.$store
        .dispatch("settings/saveFeatures", this.settings)
        .then(() => {
          this.$root.$snackbarSuccess("Feature updated");
          this.loadData();
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while updating a feature."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select.v-text-field--outlined > .v-input__control > .v-input__slot {
    min-height: auto !important;

    .v-input__append-inner {
      margin-top: auto;
      margin-bottom: auto;
    }
  }
}

.v-text-field.v-text-field--outlined ::v-deep {
  .v-input__control {
    min-height: inherit !important;

    .v-input__slot {
      padding: 0 8px 0 12px !important;

      .v-input__append-inner {
        padding-left: 0;
      }
    }
  }
}
</style>
